import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiDownload, FiGrid, FiList, FiUser, FiColumns, FiFileText, FiFilter, FiCheck, FiTrash2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';
import { clientesService, reportesService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';

const PER_PAGE = 20;

const Clientes = () => {
  const navigate = useNavigate();
  // Desactivar un cliente exige clientes.delete; sin el, el backend responde 403,
  // asi que la columna de accion no se muestra.
  const { hasPermission } = useAuth();
  const puedeEliminar = hasPermission('clientes.delete');

  // Data
  const [clientes, setClientes]         = useState([]);
  const [total, setTotal]               = useState(0);
  const [currentPage, setCurrentPage]   = useState(1);
  const [isLoading, setIsLoading]       = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore]           = useState(false);

  // UI
  const [viewMode, setViewMode]         = useState('grid');
  const [searchQuery, setSearchQuery]   = useState('');
  const [error, setError]               = useState(null);
  const [reloadKey, setReloadKey]       = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading]         = useState(false);

  // Filters
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterCategoria, setFilterCategoria]       = useState('ALL');
  const [filterTipo, setFilterTipo]                 = useState('ALL');
  const filterDropdownRef = useRef(null);

  // Infinite scroll
  const sentinelRef         = useRef(null);
  const infiniteOffsetRef   = useRef(0);
  const isLoadingMoreRef    = useRef(false);
  const hasMoreRef          = useRef(false);

  // Wave SVG
  const menuRef    = useRef(null);
  const sec1Ref    = useRef(null);
  const sec2Ref    = useRef(null);
  const sec3Ref    = useRef(null);
  const sec4Ref    = useRef(null);
  const svgPathRef = useRef(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Keep refs in sync for IntersectionObserver callback
  useEffect(() => { isLoadingMoreRef.current = isLoadingMore; }, [isLoadingMore]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  // ── Wave SVG ──────────────────────────────────────────────────────────────
  const buildWavePath = useCallback(() => {
    if (!menuRef.current || !sec1Ref.current || !sec2Ref.current || !sec3Ref.current || !sec4Ref.current || !svgPathRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const W = menuRect.width;
    if (W === 0) return;
    const VB = 1000, TOP = 3, BOT = 53, half = 18, scale = VB / W;
    const b1 = (sec1Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b2 = (sec2Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b3 = (sec3Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const d = [
      `M 0,${TOP}`,
      `L ${b1 - half},${TOP}`,
      `C ${b1},${TOP} ${b1},${BOT} ${b1 + half},${BOT}`,
      `L ${b2 - half},${BOT}`,
      `C ${b2},${BOT} ${b2},${TOP} ${b2 + half},${TOP}`,
      `L ${b3 - half},${TOP}`,
      `C ${b3},${TOP} ${b3},${BOT} ${b3 + half},${BOT}`,
      `L ${VB},${BOT}`,
    ].join(' ');
    svgPathRef.current.setAttribute('d', d);
  }, []);

  useEffect(() => {
    if (!menuRef.current) return;
    buildWavePath();
    const t = setTimeout(buildWavePath, 100);
    const ro = new ResizeObserver(buildWavePath);
    [menuRef, sec1Ref, sec2Ref, sec3Ref, sec4Ref].forEach(r => { if (r.current) ro.observe(r.current); });
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [buildWavePath, isLoading]);

  // ── Click-outside filter dropdown ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target))
        setShowFilterDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Transform ─────────────────────────────────────────────────────────────
  const transformClienteFromAPI = useCallback((cliente) => ({
    id: cliente.id,
    nombreNegocio: cliente.company_name || cliente.full_name,
    nombreContacto: cliente.full_name || '',
    telefono: cliente.phone || cliente.primary_phone || cliente.mobile || '',
    email: cliente.email || '',
    categoria: cliente.client_category ? (cliente.client_category === 'DISTRIBUTOR' ? 'distributor' : 'tienda') : '',
    tipoCliente: cliente.business_type || '',
    vendedorAsignado: cliente.assigned_user_id ? cliente.assigned_user_id.toString() : '',
    fechaRegistro: cliente.created_at || '',
    ultimaCompra: cliente.last_purchase || '',
    totalCompras: cliente.total_purchases || 0,
    facturasPendientes: cliente.pending_invoices || 0,
    montoFacturasPendientes: cliente.pending_amount || 0,
    status: cliente.is_active ? 'activo' : 'inactivo',
  }), []);

  // ── Build API params ───────────────────────────────────────────────────────
  const buildParams = useCallback((skip) => {
    const params = { skip, limit: PER_PAGE, is_active: true };
    if (debouncedSearchQuery.trim()) params.search = debouncedSearchQuery.trim();
    if (filterCategoria === 'distributor') params.client_category = 'DISTRIBUTOR';
    else if (filterCategoria === 'tienda') params.client_category = 'TIENDA';
    if (filterTipo !== 'ALL') params.business_type = filterTipo;
    return params;
  }, [debouncedSearchQuery, filterCategoria, filterTipo]);

  // ── Initial load / reset on filter or view change ─────────────────────────
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setCurrentPage(1);
    infiniteOffsetRef.current = 0;

    const load = async () => {
      try {
        const result = await clientesService.getClientes(buildParams(0));
        if (cancelled) return;
        if (result.success) {
          const items = result.data.map(transformClienteFromAPI);
          setClientes(items);
          setTotal(result.total || 0);
          infiniteOffsetRef.current = items.length;
          const more = items.length === PER_PAGE && items.length < (result.total || 0);
          setHasMore(more);
          hasMoreRef.current = more;
        } else {
          setError(result.message || 'Error al cargar clientes');
          setClientes([]);
        }
      } catch {
        if (!cancelled) {
          setError('Error de conexión al cargar clientes');
          setClientes([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [debouncedSearchQuery, filterCategoria, filterTipo, viewMode, reloadKey, buildParams, transformClienteFromAPI]);

  // ── Table: change page ─────────────────────────────────────────────────────
  const handlePageChange = useCallback(async (newPage) => {
    setIsLoading(true);
    try {
      const result = await clientesService.getClientes(buildParams((newPage - 1) * PER_PAGE));
      if (result.success) {
        setClientes(result.data.map(transformClienteFromAPI));
        setTotal(result.total || 0);
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch {
      setError('Error al cambiar de página');
    } finally {
      setIsLoading(false);
    }
  }, [buildParams, transformClienteFromAPI]);

  // ── Infinite scroll: load more ─────────────────────────────────────────────
  const loadMoreInfinite = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMoreRef.current) return;
    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    try {
      const result = await clientesService.getClientes(buildParams(infiniteOffsetRef.current));
      if (result.success) {
        const items = result.data.map(transformClienteFromAPI);
        setClientes(prev => [...prev, ...items]);
        setTotal(result.total || 0);
        infiniteOffsetRef.current += items.length;
        const more = infiniteOffsetRef.current < (result.total || 0);
        setHasMore(more);
        hasMoreRef.current = more;
      }
    } catch {
      console.error('Error loading more clientes');
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [buildParams, transformClienteFromAPI]);

  // ── IntersectionObserver ───────────────────────────────────────────────────
  useEffect(() => {
    if (viewMode === 'table' || !sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreInfinite(); },
      { rootMargin: '120px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [viewMode, loadMoreInfinite]);

  // ── Delete (soft) ─────────────────────────────────────────────────────────
  const handleDeleteCliente = useCallback(async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('¿Desactivar este cliente? Seguirá en la base de datos como inactivo.')) return;
    try {
      const result = await clientesService.deleteCliente(id);
      if (result.success) {
        setClientes(prev => prev.filter(c => c.id !== id));
        setTotal(prev => Math.max(0, prev - 1));
      }
    } catch {
      console.error('Error al desactivar cliente');
    }
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const handleAddNew     = () => navigate('/system/clientes/nuevo');
  const handleDownload   = () => setShowDownloadModal(true);
  const handleClearError = () => { setError(null); setReloadKey(k => k + 1); };
  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handleClearSearch  = () => setSearchQuery('');
  const getThumbnailType   = () => 'blue-dark-gradient';

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);
    try {
      const result = await reportesService.exportarClientes({
        format,
        date_from: startDate,
        date_to: endDate,
        is_active: true,
      });
      if (result.success) {
        const mimeTypes = { csv: 'text/csv', pdf: 'application/pdf', excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };
        const extensions = { csv: 'csv', pdf: 'pdf', excel: 'xlsx' };
        const blob = new Blob([result.data], { type: mimeTypes[format] || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = startDate && endDate
          ? `clientes_${startDate}_${endDate}.${extensions[format]}`
          : `clientes_todos_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setShowDownloadModal(false);
        alert(`Reporte descargado exitosamente en formato ${format.toUpperCase()}`);
      } else {
        throw new Error(result.message || 'Error al generar el reporte');
      }
    } catch {
      alert('Error al descargar el reporte. Por favor intente nuevamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  // Tipos de negocio: lista FIJA. Antes se derivaba de los clientes cargados, así
  // que al filtrar por categoría la lista se reducía y se ocultaban opciones.
  const tiposNegocio = [
    { id: 'ALL',           label: 'Todos'         },
    { id: 'BETEL_NATURAL', label: 'Betel Natural' },
    { id: 'NATURALANDLAB', label: 'NaturalAndLab' },
    { id: 'AMBOS',         label: 'Ambos'         },
  ];
  const hayFiltrosActivos = filterCategoria !== 'ALL' || filterTipo !== 'ALL';
  const totalPages        = Math.ceil(total / PER_PAGE);

  // ── Render ────────────────────────────────────────────────────────────────
  if (isLoading && clientes.length === 0) {
    return <LoadingGrid message="Cargando clientes..." />;
  }

  return (
    <div className="clientes-page">
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button className="btn btn--ghost btn--sm" onClick={handleClearError} style={{ marginLeft: '10px' }}>
              Reintentar
            </button>
          </div>
        </div>
      )}

      <h1 className="page-title">CLIENTES</h1>

      {/* ── Action menu ── */}
      <div className="page-header-with-menu">
        <div className="action-menu" ref={menuRef}>
          <svg className="action-bar-wave" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 56" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="waveGradClientes" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%"   stopColor="#a64b95" stopOpacity="0.75" />
                <stop offset="45%"  stopColor="#c975b9" stopOpacity="0.75" />
                <stop offset="100%" stopColor="#7e2371" stopOpacity="0.75" />
              </linearGradient>
            </defs>
            <path ref={svgPathRef} d="" fill="none" stroke="url(#waveGradClientes)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>

          <button className="menu-item menu-item--first" ref={sec1Ref} onClick={handleAddNew}>
            <span className="add-btn-plus">+</span>
            <span className="add-btn-label">AGREGAR CLIENTE</span>
          </button>

          <div className="menu-item menu-item--search" ref={sec2Ref}>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar cliente..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <div className="menu-item menu-item--views" ref={sec3Ref}>
            <button className={`view-btn ${viewMode === 'grid'  ? 'active' : ''}`} onClick={() => setViewMode('grid')}  title="Vista de tarjetas"><FiGrid /></button>
            <button className={`view-btn ${viewMode === 'list'  ? 'active' : ''}`} onClick={() => setViewMode('list')}  title="Vista de lista"><FiList /></button>
            <button className={`view-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')} title="Vista de tabla"><FiColumns /></button>
          </div>

          <div className="menu-item menu-item--checkbox" ref={sec4Ref}>
            <div className="filter-dropdown-wrapper" ref={filterDropdownRef}>
              <button
                className={`view-btn ${hayFiltrosActivos ? 'active' : ''}`}
                onClick={() => setShowFilterDropdown(prev => !prev)}
                title="Filtrar clientes"
              >
                <FiFilter />
              </button>
              {showFilterDropdown && (
                <div className="filter-popover">
                  <div style={{ padding: '6px 16px 4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px' }}>CATEGORÍA</div>
                  {[
                    { id: 'ALL',         label: 'Todas'        },
                    { id: 'distributor', label: 'Distribuidor' },
                    { id: 'tienda',      label: 'Tienda'       },
                  ].map(op => (
                    <button key={op.id} className={`filter-popover-item ${filterCategoria === op.id ? 'active' : ''}`} onClick={() => setFilterCategoria(op.id)}>
                      {filterCategoria === op.id && <FiCheck />} {op.label}
                    </button>
                  ))}

                  <div style={{ padding: '6px 16px 4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', borderTop: '1px solid #f1f5f9', marginTop: '4px' }}>TIPO DE NEGOCIO</div>
                  {tiposNegocio.map(op => (
                    <button key={op.id} className={`filter-popover-item ${filterTipo === op.id ? 'active' : ''}`} onClick={() => setFilterTipo(op.id)}>
                      {filterTipo === op.id && <FiCheck />} {op.label}
                    </button>
                  ))}

                  {hayFiltrosActivos && (
                    <button
                      className="filter-popover-item"
                      style={{ borderTop: '1px solid #f1f5f9', marginTop: '4px', color: '#7B2D8E', fontWeight: 700 }}
                      onClick={() => { setFilterCategoria('ALL'); setFilterTipo('ALL'); setShowFilterDropdown(false); }}
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
              )}
            </div>
            <button className="view-btn" onClick={handleDownload} title="Descargar reporte"><FiDownload /></button>
          </div>
        </div>
      </div>

      {/* ── Empty state ── */}
      {clientes.length === 0 && !isLoading && !error && (
        <div className="empty-state">
          <div className="empty-state-icon"><FiFileText size={48} /></div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron clientes' : 'No hay clientes registrados'}
          </h3>
          <p className="empty-state-description">
            {searchQuery
              ? `No se encontraron clientes que coincidan con "${searchQuery}"`
              : 'Comienza agregando tu primer cliente al sistema'}
          </p>
          {searchQuery && (
            <p><button className="btn btn--ghost" onClick={handleClearSearch}>Limpiar búsqueda</button></p>
          )}
        </div>
      )}

      {/* ── TABLE VIEW ── */}
      {clientes.length > 0 && viewMode === 'table' && (
        <>
          <div className="table-container">
            {isLoading && (
              <div className="table-loading-overlay">
                <span>Cargando...</span>
              </div>
            )}
            <div className="table-scroll">
            <table className="data-table data-table--clientes">
              <thead>
                <tr>
                  <th>NOMBRE DEL NEGOCIO</th>
                  <th>CONTACTO</th>
                  <th>TELÉFONO</th>
                  <th>EMAIL</th>
                  <th className="text-center">CATEGORÍA</th>
                  <th className="text-center">TIPO</th>
                  <th className="text-center">FACT. PENDIENTES</th>
                  <th className="text-center">ESTADO</th>
                  <th className="text-center">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((cliente) => (
                  <tr key={cliente.id} onClick={() => navigate(`/system/clientes/${cliente.id}`)} style={{ cursor: 'pointer' }}>
                    <td><strong>{cliente.nombreNegocio}</strong></td>
                    <td>{cliente.nombreContacto}</td>
                    <td>{cliente.telefono || '-'}</td>
                    <td>{cliente.email || '-'}</td>
                    <td className="text-center">
                      <span className={`badge badge-${cliente.categoria}`}>
                        {cliente.categoria === 'distributor' ? 'Distribuidor' : 'Tienda'}
                      </span>
                    </td>
                    <td className="text-center">{cliente.tipoCliente || '-'}</td>
                    <td className="text-center">
                      {cliente.facturasPendientes > 0
                        ? <span className="badge badge-warning">{cliente.facturasPendientes}</span>
                        : '-'}
                    </td>
                    <td className="text-center">
                      <span className={`badge badge-${cliente.status}`}>
                        {cliente.status === 'activo' ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="text-center" onClick={e => e.stopPropagation()}>
                      {puedeEliminar && (
                        <button
                          className="btn-table-delete"
                          onClick={e => handleDeleteCliente(e, cliente.id)}
                          title="Desactivar cliente"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>{/* /table-scroll */}
          </div>

          {/* Pagination — siempre visible con info, controles solo si hay >1 página */}
          <div className="pagination-bar">
            <span className="pagination-info">
              {total > 0
                ? `${(currentPage - 1) * PER_PAGE + 1}–${Math.min(currentPage * PER_PAGE, total)} de ${total} clientes`
                : `${clientes.length} clientes`}
            </span>
            {totalPages > 1 && (
              <div className="pagination-controls">
                <button className="page-btn" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                  <FiChevronLeft size={14} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && <span className="page-dots">…</span>}
                      <button
                        className={`page-btn${p === currentPage ? ' active' : ''}`}
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                <button className="page-btn" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                  <FiChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── GRID / LIST VIEW (infinite scroll) ── */}
      {clientes.length > 0 && (viewMode === 'grid' || viewMode === 'list') && (
        <>
          <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
            {clientes.map((cliente) => (
              <div
                key={cliente.id}
                className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
                onClick={() => navigate(`/system/clientes/${cliente.id}`)}
                style={{ cursor: 'pointer' }}
              >
                {viewMode === 'grid' ? (
                  <>
                    <div className={`content-thumbnail ${getThumbnailType(cliente.categoria)}`}>
                      <FiUser className="thumbnail-icon" />
                    </div>
                    <div className="content-info">
                      <div className="content-title">{cliente.nombreNegocio}</div>
                      <div className="content-subtitle">{cliente.nombreContacto}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`content-thumbnail-small ${getThumbnailType(cliente.categoria)}`}>
                      <FiUser className="thumbnail-icon-small" />
                    </div>
                    <div className="content-info-list">
                      <div className="content-title">{cliente.nombreNegocio}</div>
                      <div className="content-subtitle">{cliente.nombreContacto}</div>
                      <div className="content-meta">
                        <span className="meta-item">Tel: {cliente.telefono}</span>
                        <span className="meta-item">{cliente.categoria}</span>
                        {cliente.facturasPendientes > 0 && (
                          <span className="meta-item warning">Facturas pendientes: {cliente.facturasPendientes}</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Sentinel for IntersectionObserver */}
          <div ref={sentinelRef} style={{ height: 1 }} />

          {isLoadingMore && (
            <div className="infinite-scroll-loading">
              <span className="infinite-scroll-dot" />
              <span className="infinite-scroll-dot" />
              <span className="infinite-scroll-dot" />
            </div>
          )}

          {!hasMore && clientes.length > 0 && (
            <div className="infinite-scroll-end">
              {total} {total === 1 ? 'cliente' : 'clientes'} en total
            </div>
          )}
        </>
      )}

      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title="Descargar Reporte de Clientes"
        moduleName="clientes"
        isLoading={isDownloading}
      />
    </div>
  );
};

export default Clientes;
